<script setup lang="ts">
import type { StyleValue } from 'vue';
import CardTermsAndConditions from './CardTermsAndConditions.vue';
import type TripPackage from '@itinerary/trip/TripPackage';
import { ReserveType } from '@utils/itinerary';
import { SearchMode } from '@utils/types';
import { TripTermsDocument } from '@schema/directus';

const { tripPackage } = defineProps<{
    tripPackage: TripPackage;
}>();
const currentSearchForm = useCurrentSearchForm();
const { searchMode, isDomestic } = moduleToRefs(currentSearchForm.value);
const searchFormStore = useSearchFormStore();
const { reserveType } = storeToRefs(searchFormStore);
const item = ref('');
const additionalRules = ref<string[]>([]);
const { load } = useLazyQuery(TripTermsDocument);
const extraCancellationTerms = computed(() => {
    if (tripPackage.separateExtraTermsTab) {
        return [];
    }
    const { extraCancellationTerm } = tripPackage;
    if (extraCancellationTerm) {
        return [extraCancellationTerm];
    }
    return [];
});
const itemDir = computed<StyleValue>(() => {
    if (
        reserveType.value === ReserveType.Flight &&
        tripPackage.international &&
        item.value.includes('1)')
    ) {
        return { direction: 'ltr' };
    }
    return { direction: 'rtl' };
});
const enNumbers = computed(
    () => reserveType.value === ReserveType.Flight && !isDomestic.value
);
const message = computed(() => tripPackage.cancellationMessage);
const loadAdditionalRules = async () => {
    const localAdditionalRules: string[] = [];
    try {
        await Promise.all(
            tripPackage.allGroupedTrips.map(async trip => {
                const rules = await load(undefined, {
                    Bus: reserveType.value === 'bus',
                    Flight: reserveType.value === 'flight',
                    Train: reserveType.value === 'train',
                    DepartureTime: trip.departureDateTime.toISOString(),
                    International: searchMode.value === SearchMode.Foreign,
                    Carrier: trip.carrierCode,
                });
                if (rules) {
                    localAdditionalRules.push(
                        ...rules.TripTerms?.map(
                            rule => rule.Description || ''
                        ).filter(
                            rule =>
                                !localAdditionalRules.length ||
                                localAdditionalRules.some(
                                    r => !r.includes(rule) && !rule.includes(r)
                                )
                        )
                    );
                }
            })
        );
    } catch (e) {
        reportException(e);
    }
    additionalRules.value = [
        ...localAdditionalRules,
        ...extraCancellationTerms.value,
    ];
};
onBeforeMount(() => loadAdditionalRules());
onMounted(() => {
    if (!tripPackage.loadableTermsUrl) {
        item.value = tripPackage.termsAndConditions || '';
    }
});
</script>

<template>
    <div
        v-if="item || tripPackage.loadableTermsUrl || additionalRules.length"
        class="terms"
    >
        <mr-typography
            v-if="message"
            weight="500"
            size="small"
            color="var(--cool-gray-800)"
        >
            {{ message }}
        </mr-typography>
        <template v-if="!tripPackage.cancellationTerms.length">
            <CardTermsAndConditions
                v-if="tripPackage.loadableTermsUrl"
                :url="tripPackage.loadableTermsUrl"
            />
            <ul v-else class="terms-items-container">
                <li class="terms-item">
                    <span
                        class="terms-item-text"
                        :class="{ 'en-numbers': enNumbers }"
                        :style="itemDir"
                        v-html="item"
                    />
                </li>
            </ul>
        </template>
        <template v-else>
            <div class="cancellation-terms">
                <div
                    v-for="(term, key) in tripPackage.cancellationTerms"
                    :key
                    class="cancellation-term"
                >
                    <div class="title">استرداد {{ term.title }}</div>
                    <div
                        class="penalty"
                        :class="{ zero: term.isZero, full: term.isFull }"
                    >
                        <template v-if="term.specifiedByProvider">
                            مطابق قوانین تأمین‌کننده
                        </template>
                        <template v-else-if="term.isFull">
                            غیرقابل استرداد
                        </template>
                        <template v-else-if="term.isZero">
                            بدون جریمه
                        </template>
                        <template v-else>
                            {{ term.amount }}
                            <span>جریمه</span>
                        </template>
                    </div>
                </div>
            </div>
        </template>
        <div v-if="additionalRules.length" class="additional-rules-container">
            <svg width="20" height="20" viewBox="0 0 20 20" class="alert-icon">
                <path
                    d="M9 13H11V15H9V13ZM9 5H11V11H9V5ZM9.99 0C4.47 0 0 4.48 0 10C0 15.52 4.47 20 9.99 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 9.99 0ZM10 18C5.58 18 2 14.42 2 10C2 5.58 5.58 2 10 2C14.42 2 18 5.58 18 10C18 14.42 14.42 18 10 18Z"
                />
            </svg>
            <ul class="additional-rules">
                <li v-for="rule in additionalRules" :key="rule">
                    <span>{{ rule }}</span>
                </li>
            </ul>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.terms {
    display: flex;
    flex-direction: column;
    padding: 20px;
    // width: 100%;
    // height: 100%;
    @include mobile-only(panel) {
        padding: 10px 16px;
        border-radius: 10px;
        background-color: white;
        height: fit-content;
    }
    .cancellation-terms {
        display: flex;
        @include f-gap(16px, row, wrap);
        flex-wrap: wrap;
        @include mobile-only {
            margin: 0;
        }
        .cancellation-term {
            width: 153px;
            display: flex;
            flex-direction: column;
            padding: 16px 6px 12px;
            border-radius: 16px;
            border: 2px dashed usePalette($p-gray, $p-500);
            @include f-gap(8px, column);
            @include mobile-only {
                width: 100%;
                flex-direction: row;
                justify-content: space-between;
                padding: 16px;
            }
            .title {
                @include tg-m($w5);
                line-height: 24px; // @warning: property out of design system
                text-align: center;
                height: 72px;
                display: flex;
                justify-content: center;
                @include mobile-only {
                    height: 48px;
                    align-items: center;
                    margin: 0;
                    text-align: right;
                }
            }
            .penalty {
                @include tg-xl($w7, usePalette($p-red));
                text-align: center;
                @include mobile-only {
                    margin: 0;
                    display: flex;
                    align-items: center;
                }
                &.zero {
                    color: usePalette($p-green);
                }
                &.full {
                    // same as base, but shows that it can be overridden
                    color: usePalette($p-red);
                }
                span {
                    @include tg-m($w7, usePalette($p-red));
                    @include mobile-only {
                        display: none;
                    }
                }
            }
        }
    }
    .terms-items-container {
        margin: 0;
        padding: 0;
        height: 100%;
        @include mobile-only {
            height: auto;
        }
        .terms-item {
            display: flex;
            flex-direction: row;
            list-style-type: none;
            margin: 3px 0;
            .terms-item-text {
                margin-top: 16px;
                font-style: normal;
                @include font-weight();
                font-size: 14px;
                line-height: 22px;
                text-align: justify;
                color: usePalette($p-cool-gray, $p-800);
                white-space: pre-wrap;
                &.en-numbers {
                    @include en-num;
                }
            }
        }
    }
    .additional-rules-container {
        display: flex;
        margin-top: 8px;
        padding: 16px;
        background-color: usePalette($p-yellow, $p-200);
        border-radius: 10px;
        .alert-icon {
            fill: usePalette($p-yellow);
        }
        .additional-rules {
            margin: 0;
            width: 100%;
            li {
                font-size: 14px;
                @include font-weight();
            }
        }
    }
}
</style>
